import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import { Section, Container, Block, Heading, Paragraph } from '../devlink/_Builtin';
import { MortgageFormData, MortgageResult, FORM_STEPS } from './types/mortgage';
import { calculateMortgageAffordability } from './utils/mortgageCalculator';
import FormStep1 from './components/FormStep1';
import FormStep2 from './components/FormStep2';
import FormStep3 from './components/FormStep3';
import ResultsScreen from './components/ResultsScreen';
import ProgressIndicator from './components/ProgressIndicator';

// Simple validation functions
const validateStep1 = (data: Partial<MortgageFormData>) => {
  const errors: Record<string, string> = {};
  
  if (!data.propertyRegion) {
    errors.propertyRegion = 'Property region is required';
  }
  
  if (data.mortgageTermYears !== undefined && (data.mortgageTermYears < 1 || data.mortgageTermYears > 35)) {
    errors.mortgageTermYears = 'Please enter a valid mortgage term (1-35 years)';
  }
  
  if (data.mortgageTermMonths !== undefined && (data.mortgageTermMonths < 0 || data.mortgageTermMonths > 11)) {
    errors.mortgageTermMonths = 'Please enter a valid mortgage term (0-11 months)';
  }
  
  if (data.interestRate !== undefined && (data.interestRate < 0.1 || data.interestRate > 15)) {
    errors.interestRate = 'Please enter a valid interest rate (0.1-15%)';
  }
  
  if (data.revertRate !== undefined && (data.revertRate < 0.1 || data.revertRate > 15)) {
    errors.revertRate = 'Please enter a valid revert rate (0.1-15%)';
  }
  
  if (!data.productType) {
    errors.productType = 'Product type is required';
  }
  
  return errors;
};

const validateStep2 = (data: Partial<MortgageFormData>) => {
  const errors: Record<string, string> = {};
  
  if (data.currentAge !== undefined && (data.currentAge < 18 || data.currentAge > 80)) {
    errors.currentAge = 'Please enter a valid age (18-80)';
  }
  
  if (data.retirementAge !== undefined && (data.retirementAge < 55 || data.retirementAge > 75)) {
    errors.retirementAge = 'Please enter a valid retirement age (55-75)';
  }
  
  if (data.employedIncome !== undefined && data.employedIncome < 0) {
    errors.employedIncome = 'Please enter a valid employed income';
  }
  
  if (data.selfEmployedIncome !== undefined && data.selfEmployedIncome < 0) {
    errors.selfEmployedIncome = 'Please enter a valid self-employed income';
  }
  
  if (data.pensionIncome !== undefined && data.pensionIncome < 0) {
    errors.pensionIncome = 'Please enter a valid pension income';
  }
  
  return errors;
};

const validateStep3 = (data: Partial<MortgageFormData>) => {
  const errors: Record<string, string> = {};
  
  if (data.leisureExpenses !== undefined && data.leisureExpenses < 0) {
    errors.leisureExpenses = 'Please enter a valid leisure expenses amount';
  }
  
  if (data.loanRepayments !== undefined && data.loanRepayments < 0) {
    errors.loanRepayments = 'Please enter a valid loan repayments amount';
  }
  
  if (data.householdExpenses !== undefined && data.householdExpenses < 0) {
    errors.householdExpenses = 'Please enter a valid household expenses amount';
  }
  
  if (data.dependentsCosts !== undefined && data.dependentsCosts < 0) {
    errors.dependentsCosts = 'Please enter a valid dependents costs amount';
  }
  
  return errors;
};

const initialFormData: Partial<MortgageFormData> = {
  propertyRegion: undefined,
  mortgageTermYears: undefined,
  mortgageTermMonths: undefined,
  interestRate: undefined,
  revertRate: undefined,
  productType: undefined,
  currentAge: undefined,
  retirementAge: undefined,
  employedIncome: undefined,
  selfEmployedIncome: undefined,
  pensionIncome: undefined,
  leisureExpenses: undefined,
  loanRepayments: undefined,
  householdExpenses: undefined,
  dependentsCosts: undefined,
};

function App() {
  const [currentStep, setCurrentStep] = useState(1);
  const [result, setResult] = useState<MortgageResult | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<Partial<MortgageFormData>>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const updateFormData = (field: keyof MortgageFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateCurrentStep = () => {
    let stepErrors: Record<string, string> = {};
    
    switch (currentStep) {
      case 1:
        stepErrors = validateStep1(formData);
        break;
      case 2:
        stepErrors = validateStep2(formData);
        break;
      case 3:
        stepErrors = validateStep3(formData);
        break;
    }
    
    setErrors(stepErrors);
    return Object.keys(stepErrors).length === 0;
  };

  const nextStep = () => {
    const isValid = validateCurrentStep();
    
    if (isValid) {
      if (currentStep < FORM_STEPS.length) {
        setCurrentStep(currentStep + 1);
        setErrors({}); // Clear errors when moving to next step
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const onSubmit = async () => {
    // Validate ALL steps before submitting
    const step1Errors = validateStep1(formData);
    const step2Errors = validateStep2(formData);
    const step3Errors = validateStep3(formData);
    
    const allErrors = { ...step1Errors, ...step2Errors, ...step3Errors };
    setErrors(allErrors);
    
    if (Object.keys(allErrors).length > 0) {
      return; // Don't submit if any validation fails
    }
    
    setIsSubmitting(true);
    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const calculationResult = calculateMortgageAffordability(formData as MortgageFormData);
      setResult(calculationResult);
    } catch (error) {
      console.error('Error calculating mortgage:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setCurrentStep(1);
    setResult(null);
    setFormData(initialFormData);
    setErrors({});
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return <FormStep1 formData={formData} errors={errors} updateFormData={updateFormData} />;
      case 2:
        return <FormStep2 formData={formData} errors={errors} updateFormData={updateFormData} />;
      case 3:
        return <FormStep3 formData={formData} errors={errors} updateFormData={updateFormData} />;
      default:
        return <FormStep1 formData={formData} errors={errors} updateFormData={updateFormData} />;
    }
  };

  if (result) {
    return (
      <Section>
        <Container>
          <ResultsScreen result={result} onReset={resetForm} />
        </Container>
      </Section>
    );
  }

  return (
    <Section>
      <Container>
        <Block>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Heading tag="h1">Mortgage Affordability Calculator</Heading>
            <Paragraph>
              Find out how much you could borrow in just a few simple steps
            </Paragraph>
          </motion.div>

          <ProgressIndicator currentStep={currentStep} totalSteps={FORM_STEPS.length} />

          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {renderCurrentStep()}
            </motion.div>
          </AnimatePresence>

          <div>
            {currentStep > 1 && (
              <button
                type="button"
                onClick={prevStep}
              >
                Previous
              </button>
            )}
            
            {currentStep < FORM_STEPS.length ? (
              <button
                type="button"
                onClick={nextStep}
              >
                Next
              </button>
            ) : (
              <button
                type="button"
                onClick={onSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Calculating...' : 'Calculate Affordability'}
              </button>
            )}
          </div>
        </Block>
      </Container>
    </Section>
  );
}

export default App; 